import React, { FC, useCallback } from "react";
import PanelBackButton from "containers/RightPanel/components/PanelBackButton";
import Button from "components/Button/Button";
import {
  ContentWrapper,
  Heading,
} from "containers/RightPanel/RightPanel.styles";
import { ButtonColor, ButtonType, ButtonVariant } from "enums/Button";
import { useFormik } from "formik";
import { Map } from "immutable";
import { useTranslation } from "react-i18next";
import InputMask from "react-input-mask";
import { connect } from "react-redux";
import {
  createFieldBlurHandler,
  createMaskedFieldBlurHandler,
} from "utils/formValidation";
import {
  phoneNumberFormatChars,
  phoneNumbersMasks,
} from "../../../../components/Addresses/addressFormHelper";
import {
  ButtonsWrapper,
  Content,
  FormWrapper,
} from "../../../../components/Addresses/components/AddressForm.styles";
import Input from "../../../../components/Input/Input";
import {
  createInboundAddress,
  updateInboundAddress,
} from "../../../../reduxFolder/reducers/inbound";
import {
  closeRightPanel,
  openRightPanel,
} from "../../../../reduxFolder/reducers/rightPanel";
import { OpenRightPanel } from "../../../../types/common/rightPanel";
import { FieldWrapper } from "../../../../styles/formStyles";
import { inboundAddressFormik } from "./inboundAddressFormHelper";

interface InboundAddressFormPanelProps {
  panelData: any;
  createInboundAddress: (data: { inbound_address: any }) => Promise<any>;
  updateInboundAddress: (
    id: number,
    data: { inbound_address: any },
  ) => Promise<any>;
  openRightPanel: OpenRightPanel;
  closeRightPanel: () => void;
}

const InboundAddressFormPanel: FC<InboundAddressFormPanelProps> = React.memo(
  ({
    panelData,
    createInboundAddress,
    updateInboundAddress,
    openRightPanel,
    closeRightPanel,
  }) => {
    const { t } = useTranslation("common");
    const isEditForm = panelData.get("isEditForm");
    const returnPreviousPanel = panelData.get("returnPreviousPanel");

    const formProps = useFormik(
      inboundAddressFormik(
        t,
        panelData,
        createInboundAddress,
        updateInboundAddress,
        openRightPanel,
        closeRightPanel,
      ),
    );
    const { values, errors, touched, handleChange, handleSubmit, isSubmitting } =
      formProps;
    const handleBlur = createFieldBlurHandler(formProps);
    const handlePhoneBlur = createMaskedFieldBlurHandler(
      formProps,
      phoneNumbersMasks.GENERIC,
      " ",
      phoneNumberFormatChars,
    );

    const handleBack = useCallback(
      () =>
        openRightPanel(
          returnPreviousPanel,
          panelData.get("initialPanelData", Map()),
        ),
      [panelData, returnPreviousPanel, openRightPanel],
    );

    const textInput = (
      name: keyof typeof values,
      labelKey: string,
    ) => (
      <Input
        placeholder={t(labelKey)}
        name={name}
        value={values[name]}
        onChange={handleChange}
        onBlur={handleBlur}
        tooltip={errors[name]}
        invalid={errors[name] && touched[name]}
      />
    );

    return (
      <ContentWrapper>
        <Heading>
          {returnPreviousPanel && (
            <PanelBackButton onClick={handleBack} />
          )}
          {isEditForm
            ? t("inbound.addresses.editingTitle")
            : t("inbound.addresses.addingTitle")}
        </Heading>
        <FormWrapper onSubmit={handleSubmit}>
          <Content>
            <FieldWrapper>
              {textInput("name", "inbound.addresses.name")}
            </FieldWrapper>
            <FieldWrapper>
              <InputMask
                type="tel"
                placeholder={t("inbound.addresses.phone")}
                name="phone"
                value={values.phone}
                onChange={handleChange}
                onBlur={handlePhoneBlur}
                tooltip={errors.phone}
                invalid={errors.phone && touched.phone}
                formatChars={phoneNumberFormatChars}
                maskChar=" "
                mask={phoneNumbersMasks.GENERIC}
              >
                {/* @ts-ignore */}
                {(inputProps) => <Input {...inputProps} />}
              </InputMask>
            </FieldWrapper>
            <FieldWrapper>
              {textInput("addressLine1", "inbound.addresses.addressLine1")}
            </FieldWrapper>
            <FieldWrapper>
              {textInput("addressLine2", "inbound.addresses.addressLine2")}
            </FieldWrapper>
            <FieldWrapper>
              {textInput("city", "inbound.addresses.city")}
              {textInput("state", "inbound.addresses.state")}
            </FieldWrapper>
            <FieldWrapper>
              {textInput("zipCode", "inbound.addresses.zipCode")}
            </FieldWrapper>
          </Content>
          <ButtonsWrapper>
            <Button onClick={returnPreviousPanel ? handleBack : closeRightPanel}>
              {t("common.cancel")}
            </Button>
            <Button
              isLoading={isSubmitting}
              disabled={isSubmitting}
              type={ButtonType.Submit}
              color={ButtonColor.Primary}
              variant={ButtonVariant.Filled}
            >
              {t("common.saveChanges")}
            </Button>
          </ButtonsWrapper>
        </FormWrapper>
      </ContentWrapper>
    );
  },
);

const withConnect = connect(null, {
  createInboundAddress,
  updateInboundAddress,
  openRightPanel,
  closeRightPanel,
});

export default withConnect(InboundAddressFormPanel);
